import {
  Box,
  Stack,
  Inline,
  Button,
  Toggle,
  Text,
  Columns,
  Column,
  useToast,
  TextLink,
  Heading,
  Badge,
  Skeleton
} from '@machobear/design-system'
import { Formik, Form, FormikHelpers } from 'formik'
import { useEffect } from 'react'
import * as Yup from 'yup'
import { useTranslation, TFunction } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useHistory, useParams } from 'react-router'
import noop from 'lodash/noop'
import { useQueryClient } from '@tanstack/react-query'
import { FieldInput } from '@/components/ds-form'
import useProfile from '@/hooks/use-profile'

import useSearchParams from '@/hooks/use-search-params'
import formatDate from '@/utils/format-date'
import client from '@/ports/api-client'
import PopoverUpgrade from './PopoverUpgrade'
import MessageEditor from './MessageEditor'

type CreateMessageType = {
  pageTitle: string
  onChangeID: (id: string) => void
  onChangePageTitle: (pageTitle: string) => void
  showNextStep: () => void
}

type BodyType = {
  name: string
  content: string
  attach_new_listings: number | string
  is_active: boolean | string
}

type FormValues = {
  auto_reply_title: string
  auto_reply_message: string
  set_active: boolean
  variables: string
  new_created_listing: boolean
  setSubmitting: (isSubmitting: boolean) => void
}

const createAutoReply = async (body: BodyType, trigger: string) => {
  const formData = new FormData()
  formData.append('body', JSON.stringify(body))

  const response = await client.post(`/auto_reply_trigger/content/${trigger}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response
}

const updateAutoReplyMessage = async (body: BodyType, id: string) => {
  const formData = new FormData()
  formData.append('body', JSON.stringify(body))

  const response = await client.put(`/auto_reply_trigger/content/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return response
}

const getAutoReplyMessage = async (id: string) => {
  const response = await client.get(`/auto_reply_trigger/content/${id}`)
  return response
}

const getSubmitText = (t: TFunction, isSubmitting: boolean, id?: string) => {
  if (isSubmitting) {
    return t('common.loading', 'Loading...')
  }
  return !id ? t('common.save_&_continue', 'Save & Continue') : t('common.save', 'Save')
}

const AutoReplyForm = ({
  pageTitle,
  onChangePageTitle,
  onChangeID,
  showNextStep
}: CreateMessageType) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const history = useHistory()
  const profileData = useProfile()
  const { mode }: { mode: string } = useParams()
  const isEssentialPlan = profileData?.company_info?.base_plan_name === 'essentials-plan-monthly'
  const { trigger = 'first_tenant_message_received', id }: { trigger?: string; id?: string } =
    useSearchParams()

  const showToast = useToast()

  const prefilledMessage = `${t(
    'l.settings.view.auto_reply.prefilled_message_greeting',
    'Hi [Tenant First Name]'
  )}

${t(
  'l.settings.view.auto_reply.prefilled_message',
  'Thanks for applying to [Listing Address]. I’ll get back to you as soon as possible!'
)}`

  const { data, isLoading } = useQuery({
    queryKey: ['autoreply', 'autoreply_message', id],
    queryFn: () => getAutoReplyMessage(String(id)),
    staleTime: 30000,
    enabled: !!id,
    refetchOnWindowFocus: false
  })

  const initialValues = {
    auto_reply_title: data?.name || '',
    auto_reply_message: data?.content || prefilledMessage,
    variables: '',
    set_active: mode === 'create' ? true : data?.is_active === '1',
    new_created_listing: data?.attach_new_listings === '1',
    setSubmitting: noop
  }

  const isActive = data?.is_active === '1'
  const updatedAt = !!data?.updated_at ? data?.updated_at : data?.created_at

  useEffect(() => {
    if (mode === 'edit') {
      onChangePageTitle(data?.name)
    }
  }, [data, mode, onChangePageTitle])

  const addMessage = useMutation({
    mutationFn: ({ values, trigger }: { values: BodyType; trigger: string }) => {
      return createAutoReply(values, trigger)
    },
    onError: error => {
      if (error?.message === 'Create Auto Reply Not allowed. Quota Exceeded.') {
        showToast({
          tone: 'critical',
          message: t(
            'l.settings.view.auto_reply.max_auto_reply.text',
            'Maximum number of auto-replies reached. Need more? Upgrade or contact support.'
          )
        })
        return
      }
      showToast({
        tone: 'critical',
        message: error?.message
      })
    },
    onSuccess: async (result, trigger) => {
      queryClient.invalidateQueries({
        queryKey: ['autoreply', 'autoreply_messages', result?.auto_reply_trigger_txt_id]
      })
      onChangeID(result?.id)
      onChangePageTitle(result?.name)
      showToast({
        tone: 'positive',
        message: t(
          'l.settings.view.auto_reply.auto_reply_toast_complete',
          'Your changes have been saved'
        ),
        description: (
          <Box>
            <Text tone="link">
              <TextLink href={`/settings/auto-reply/manage?trigger=${trigger.trigger}`}>
                {t('l.settings.view.auto_reply.auto_reply_toast_view_message', 'View messages')}
              </TextLink>
            </Text>
          </Box>
        )
      })

      showNextStep()

      history.push(
        `/settings/auto-reply/auto-reply-message/edit?trigger=${trigger.trigger}&id=${result?.id}`
      )
    }
  })

  const updateMessage = useMutation({
    mutationFn: ({ values, id }: { values: BodyType; id: string }) => {
      return updateAutoReplyMessage(values, id)
    },
    onSuccess: async result => {
      queryClient.invalidateQueries({
        queryKey: ['autoreply', 'autoreply_messages', result?.auto_reply_trigger_txt_id]
      })
      queryClient.invalidateQueries({
        queryKey: ['autoreply', 'autoreply_message', id]
      })
      onChangePageTitle(result?.name)
      showToast({
        tone: 'positive',
        message: t(
          'l.settings.view.auto_reply.auto_reply_toast_complete',
          'Your changes have been saved'
        ),
        description: (
          <Box>
            <Text tone="link">
              <TextLink
                href={`/settings/auto-reply/manage?trigger=${result?.auto_reply_trigger_txt_id}`}
              >
                {t('l.settings.view.auto_reply.auto_reply_toast_view_message', 'View messages')}
              </TextLink>
            </Text>
          </Box>
        )
      })
    }
  })

  const onSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ): Promise<void> => {
    setSubmitting(true)
    if (id) {
      updateMessage.mutate({
        values: {
          name: values.auto_reply_title,
          content: values.auto_reply_message,
          attach_new_listings: values.new_created_listing ? '1' : '0',
          is_active: values.set_active ? '1' : '0'
        },
        id
      })
    } else {
      addMessage.mutate({
        values: {
          name: values.auto_reply_title,
          content: values.auto_reply_message,
          attach_new_listings: values.new_created_listing ? 1 : 0,
          is_active: values.set_active
        },
        trigger
      })
    }
  }

  const validationSchema = Yup.object().shape({
    auto_reply_title: Yup.string().required(),
    auto_reply_message: Yup.string().required().max(4000),
    variables: Yup.string(),
    set_active: Yup.boolean(),
    new_created_listing: Yup.boolean()
  })

  return (
    <>
      <Inline space="medium">
        <Heading level="4">
          {mode === 'create'
            ? t('l.settings.view.auto_reply.add_auto_reply', 'Add Auto Reply')
            : pageTitle}
        </Heading>
        {!isActive && mode !== 'create' && (
          <Badge tone="caution">{t('l.settings.view.auto_reply.inactive', 'Inactive')}</Badge>
        )}
      </Inline>

      <Formik
        onSubmit={onSubmit}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          return (
            <Form style={{ width: '100%' }}>
              <Box
                padding="large"
                border="borderNeutralLight"
                background="surface"
                style={{ borderRadius: '4px 4px 0 0' }}
              >
                <Stack space="gutter">
                  <Text size="large" weight="strong">
                    {`1. ${
                      id
                        ? t('l.settings.view.auto_reply.edit_message', 'Edit Auto Reply')
                        : t('l.settings.view.auto_reply.create_message', 'Create Message')
                    }`}
                  </Text>
                  <Columns space="large" collapseBelow="tablet">
                    <Column width="1/2">
                      {!isLoading ? (
                        <FieldInput
                          name="auto_reply_title"
                          label={t(
                            'l.settings.view.auto_reply.auto_reply_title',
                            'Auto-Reply Title'
                          )}
                          required
                          maxLength={50}
                        />
                      ) : (
                        <Skeleton.Shape height={44} width={'100%'} />
                      )}
                    </Column>
                  </Columns>

                  <MessageEditor
                    message={values.auto_reply_message}
                    onChange={message => setFieldValue('auto_reply_message', message)}
                    isLoading={isLoading}
                  />
                  {!isLoading && (
                    <>
                      <Toggle
                        id="set_active"
                        label={t('l.settings.view.auto_reply.set_as_active', 'Set as active')}
                        on={values.set_active}
                        size="small"
                        onChange={(val: any) => setFieldValue('set_active', val)}
                      />

                      <Inline space="small">
                        <Toggle
                          disabled={isEssentialPlan}
                          id="new_created_listing"
                          label={t(
                            'l.settings.view.auto_reply.newly_created_listings',
                            'Send message for my newly created listings'
                          )}
                          on={!isEssentialPlan ? values.new_created_listing : false}
                          size="small"
                          onChange={(val: any) => setFieldValue('new_created_listing', val)}
                        />
                        {isEssentialPlan && <PopoverUpgrade hasButton={false} />}
                      </Inline>
                    </>
                  )}
                </Stack>
              </Box>
              <Box
                background="neutralLight"
                padding="medium"
                border="borderNeutralLight"
                display="flex"
                alignItems="center"
                justifyContent="spaceBetween"
                style={{ borderRadius: '0 0 4px 4px' }}
              >
                <Text size="small">
                  {t('l.settings.view.auto_reply.message_last_updated', 'Message last updated: ')}
                  {!!updatedAt ? formatDate(updatedAt, 'MMM Do, YYYY') : '-'}
                </Text>

                <Inline space="xsmall" align="right" alignY="center">
                  <Button type="submit" disabled={isSubmitting} id="desktop">
                    {getSubmitText(t, isSubmitting, id)}
                  </Button>
                </Inline>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default AutoReplyForm
